import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { Avatar } from '@components/basics/Images/Avatar';
import { ReleaseMintsModal } from '@components/modals/ReleaseMintsModal';
import { YouOwnModal } from '@components/modals/YouOwnModal';
import { useAuth } from '@context/auth/base';
import { ModalContext, ModalType } from '@context/modal';
import { MintComponentSocialProofDocument, useQuery } from '@graphql/reactQuery';
import { useGetNumReleaseEditionsOwned } from '@hooks/useGetNumReleaseEditionsOwned';
import { getSingularOrPlural, preciseMillify } from '@utils/helpers';

/*#__PURE__*/ gql(/* GraphQL */ `
  query MintComponentSocialProof($releaseId: UUID!, $userId: UUID) {
    release(id: $releaseId) {
      id
      socialProofCollectors(userId: $userId) {
        id
        publicAddress
        username
        webappUri
        avatar {
          id
          url
        }
      }
      ...ReleaseUpdates
    }
  }
`);

const AVATAR_SIZE = 28;

export function SocialProofBar({
  releaseId,
  totalMinted,
}: {
  releaseId: string;
  totalMinted: number;
}) {
  const { authUser } = useAuth();
  const { openModal } = ModalContext.useContainer();

  const { data, isLoading } = useQuery(MintComponentSocialProofDocument, {
    variables: {
      releaseId,
      userId: authUser?.id,
    },
    staleTime: 0,
  });

  const release = data?.data.release;

  const socialProofCollectors = release?.socialProofCollectors;

  const { count: editionsOwned, isLoading: isLoadingEditionsOwned } = useGetNumReleaseEditionsOwned(
    { user: authUser, releaseId },
  );

  const noMints = totalMinted === 0;

  /**
   * we are reversing to show the avatars in the correct order without
   * having to use z-indexes and just use flow-row-reverse instead
   */
  const reverseSocialProofCollectors = React.useMemo(() => {
    if (!socialProofCollectors) return [];
    return [...socialProofCollectors].reverse();
  }, [socialProofCollectors]);

  const socialProofBody = noMints ? (
    <span className="mx-auto font-semibold ">Be the first to mint</span>
  ) : (
    <>
      <div
        className="group flex h-full w-full cursor-pointer items-center justify-center gap-1.5"
        onClick={() => {
          openModal(ModalType.RELEASE_MINTS, {
            body: <ReleaseMintsModal releaseId={releaseId} />,
          });
        }}
      >
        <div className="flex flex-row-reverse [&>*:nth-child(1)]:-ml-3 [&>*:nth-child(2)]:-ml-3 ">
          {reverseSocialProofCollectors.map(user => {
            return (
              <div key={user.id}>
                <Avatar
                  src={user.avatar?.url}
                  size={AVATAR_SIZE}
                  username={user.username}
                  walletAddress={user.publicAddress}
                  borderRadius="100%"
                  webappUri={user.webappUri}
                  hasBorder
                  disableLink
                />
              </div>
            );
          })}
        </div>
        <span className="font-semibold transition-[filter] duration-300 group-hover:text-base500 group-hover:brightness-70">
          {preciseMillify({
            value: totalMinted,
            precision: 3,
          })}
          &nbsp;
          {getSingularOrPlural({
            count: totalMinted,
            word: 'mint',
          })}
        </span>
      </div>

      {!!editionsOwned && authUser && (
        <button
          className="flex h-full w-full items-center justify-center gap-1.5 font-medium outline-none hover:text-base500"
          onClick={() => {
            openModal(ModalType.YOU_OWN, {
              body: <YouOwnModal releaseId={releaseId} editionsOwned={editionsOwned} />,
            });
          }}
        >
          <Avatar
            size={AVATAR_SIZE}
            src={authUser.avatar?.url}
            username={authUser.username}
            walletAddress={authUser.publicAddress}
            webappUri={authUser.webappUri}
            borderRadius="100%"
            disableLink
          />
          You own <strong>{editionsOwned}</strong>
        </button>
      )}
    </>
  );

  return (
    <div className="relative flex h-[52px] max-h-[52px] w-full items-center justify-between divide-x divide-base200 rounded-b-lg font-base text-base-s text-base800 shadow-secondary ">
      {isLoading || isLoadingEditionsOwned ? <SocialProofLoading /> : socialProofBody}
    </div>
  );
}

const SocialProofLoading = () => (
  <div className="flex h-full w-full items-center justify-center gap-1.5">
    <div className="flex flex-row-reverse [&>*:nth-child(1)]:-ml-3 [&>*:nth-child(2)]:-ml-3">
      {[...Array(3)].map((_, index) => (
        <div
          key={`SocialProofLoading-${index}`}
          className="block aspect-square h-7 w-7 min-w-[28px] animate-pulse rounded-full bg-base200"
        />
      ))}
    </div>
    <span className="h-4 w-16 animate-pulse rounded-md bg-base200" />
  </div>
);
